import { saveAs } from "file-saver";
import { validate as validateUUID } from "uuid";

export const PLACEHOLDER_FILE_ID =
  "MjAzMC0wMS0wMS82ODgwZjBkOC03MDViLTRmMTUtYTRhOC02ZTc4YTYzZDg2MGU";

export const FILE_TYPES = {
  "text/plain": "TXT",
  "application/pdf": "PDF",
  "image/png": "PNG",
  "image/jpeg": "JPEG",
  "image/webp": "WEBP",
  "text/csv": "CSV",
  "application/json": "JSON",
  "text/html": "HTML",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.ms-excel.sheet.macroEnabled.12": "XLSM",
  "application/vnd.ms-excel.sheet.binary.macroEnabled.12": "XLSB",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
  "application/msword": "DOC",
  "application/vnd.ms-outlook": "MSG",
  "message/rfc822": "EML",
  "video/mp4": "MP4",
};

/** Content type to label, keyed on the lower-cased content type. MIME types are
 *  case-insensitive (RFC 2045) and some registered ones are not all-lowercase,
 *  such as the "macroEnabled" in the .xlsm and .xlsb types: the Files API returns
 *  the canonical spelling, a locally picked file whatever the browser reports. */
const FILE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(FILE_TYPES).map(([contentType, label]) => [
    contentType.toLowerCase(),
    label,
  ]),
);

export const getFileTypeLabel = (contentType: string): string | undefined =>
  FILE_TYPE_LABELS[contentType.toLowerCase()];

export type FileSource =
  | "manual-review"
  | "case-files"
  | "case-copilot-context"
  | "agent-playground"
  | "dataset-upload"
  | "copilot";

export const FILE_EXTENSIONS = {
  TXT: "txt",
  PDF: "pdf",
  PNG: "png",
  JPG: "jpg",
  JPEG: "jpeg",
  WEBP: "webp",
  CSV: "csv",
  JSON: "json",
  HTML: "html",
  XLSX: "xlsx",
  XLSM: "xlsm",
  XLSB: "xlsb",
  DOCX: "docx",
  DOC: "doc",
  MSG: "msg",
  EML: "eml",
  MP4: "mp4",
};

export const MAX_FILE_SIZE_BYTES = 32 * 1024 * 1024; // 32MB

export const stripWrappingQuotes = (value: string): string =>
  (value.startsWith('"') && value.endsWith('"')) ||
  (value.startsWith("'") && value.endsWith("'"))
    ? value.slice(1, -1)
    : value;

export const getFilePillTitle = (value: unknown) => {
  if (typeof value !== "string") {
    return "Invalid file ID";
  }
  const fileId = stripWrappingQuotes(value);

  return fileId.length > 20
    ? `${fileId.slice(0, 6)}...${fileId.slice(-6)}`
    : fileId;
};

export const getFileNameParts = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf(".");
  const nameWithoutExt = fileName.slice(0, lastDotIndex);
  const extension = fileName.slice(lastDotIndex);
  return { lastDotIndex, nameWithoutExt, extension };
};

export const isFileId = (id: unknown): id is string => {
  if (typeof id !== "string") {
    return false;
  }

  const value = stripWrappingQuotes(id);

  if (value.length !== 63) {
    return false;
  }

  try {
    const paddedValue = value.padEnd(Math.ceil(value.length / 4) * 4, "=");

    const decoded = atob(paddedValue);
    const parts = decoded.split("/");
    return (
      parts.length === 2 &&
      // Is valid YYYY-MM-DD
      /^\d{4}-\d{2}-\d{2}$/.test(parts[0]) &&
      // Is valid UUID
      validateUUID(parts[1])
    );
  } catch {
    return false;
  }
};

/** Maps MIME types to accepted file extensions (e.g. `{ "image/jpeg": [".jpg", ".jpeg"] }`). */
export type AcceptedFileTypes = Record<string, string[]>;

/** Builds the default accept map from FILE_TYPES and FILE_EXTENSIONS. */
const buildDefaultAcceptedFileTypes = (): AcceptedFileTypes => {
  const accept: AcceptedFileTypes = {};
  for (const [mimeType, label] of Object.entries(FILE_TYPES)) {
    accept[mimeType] = Object.entries(FILE_EXTENSIONS)
      .filter(([key]) => key === label)
      .map(([, ext]) => `.${ext}`);
  }
  // image/jpeg should also accept .jpg
  if (accept["image/jpeg"] && !accept["image/jpeg"].includes(".jpg")) {
    accept["image/jpeg"].push(".jpg");
  }
  return accept;
};

/** Module-level constant for stable reference in React component default props. */
export const DEFAULT_ACCEPTED_FILE_TYPES: AcceptedFileTypes =
  buildDefaultAcceptedFileTypes();

/** Extracts a deduplicated list of bare file extensions (without leading dots) from an accept map. */
export const getExtensionsFromAcceptedFileTypes = (
  accept: AcceptedFileTypes,
): string[] => {
  const allExtensions = Object.values(accept).flat();
  const stripped = allExtensions.map((ext) => ext.replace(/^\./, ""));
  return [...new Set(stripped)];
};

export const CASE_FILE_ACCEPTED_FORMATS = getExtensionsFromAcceptedFileTypes(
  DEFAULT_ACCEPTED_FILE_TYPES,
);

export const humanReadableSize = (size: number) => {
  const units = ["bytes", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  if (size === 0) {
    return "0 bytes";
  }
  return `${size.toFixed(0)}${units[i]}`;
};

export const downloadFileFromUrl = async (
  href: string,
  fileName: string,
): Promise<void> => {
  const response = await fetch(href);
  if (!response.ok) {
    throw new Error(`Download of ${fileName} failed with ${response.status}`);
  }
  saveAs(await response.blob(), fileName);
};

/** Builds a human-readable description of accepted file types and size limit. */
export const buildFileUploadDescription = (
  accept: AcceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
  maxSizeBytes: number = MAX_FILE_SIZE_BYTES,
): string => {
  const extensions = getExtensionsFromAcceptedFileTypes(accept);
  const formattedExtensions = extensions
    .map((ext) => ext.toUpperCase())
    .join(", ");
  return `Accepted formats: ${formattedExtensions}. Max size: ${humanReadableSize(maxSizeBytes)}.`;
};
